import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { AiProviderPicker } from '../../components/ai/AiProviderPicker';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Copy, Loader2, Sparkles, Webhook } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { TextArea, TextField } from '../../components/ui/TextField';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { UpgradePrompt } from '../../components/UpgradePrompt';
import { AI_PROVIDER_LABEL, uid, type Mapping } from '../../types';
import { LimitError, aiSetup, type AIMetric } from '../../api/dataApi';
import { detectAutoFormat, parseAutoSample, webhookBodyFormat } from '../../lib/autoMapping';
import { resolveNickname } from '../../lib/mappingNickname';
import type { JsonValue, MappingSpec } from '../../lib/mappingEngine';
import { flattenPaths } from '../../lib/mappingSpecEdit';
import { buildSignalTemplate } from '../../lib/signalTemplate';
import { TEMPLATE_CHUNK, countProducedSignals, looksLikeDescription, metricPreviewSpec, metricsSpecs, planToSpec, withEntityLabel } from '../../lib/aiPlan';
import { buildReview, renameConstName } from '../../lib/aiReview';
import { NEW_CHANNEL, assignChannels } from '../../lib/aiChannels';
import { blankDraft } from '../channels/ChannelManageScreen';
import { MetricChecklist } from '../../components/integrations/MetricChecklist';
import { useOwnedTemplates } from '../../lib/useOwnedTemplates';
import { ChannelSelect, SchemaSelect } from '../../components/integrations/DestinationSelects';
import { webhookName } from '../../lib/aiWebhook';

const MAX_BYTES = 2 * 1024 * 1024;

// What the AI worked out, held for the user to look over. Nothing exists yet.
interface Proposal {
  sourceType: string; // 'webhook' | 'scrape_<format>'
  title: string;
  description?: string;
  spec: MappingSpec; // rules + format/recordPath (+ intervalSec) - channel and target are added per integration
  records: JsonValue[];
  targets: { url: string; label?: string }[]; // scrape only
  // One document holding many metrics: every list the plan also fits. spec is then the first metric's single-list mapping (what is previewed).
  metrics: AIMetric[];
  intervalSec?: number;
  notes: string[];
  produced: number;
  tried: number;
}

const cardCls = 'rounded-xl bg-black/[0.03] dark:bg-white/[0.05] p-3 flex flex-col gap-2';
const h3Cls = 'text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40 m-0';

// One box (or a dropped file), one button. The AI reads the data and proposes
// a strictly validated mapping; the page proves it on the real data, shows the
// user which field goes where and which Signals/templates it would use or
// create, and only then - on confirm - creates the templates and integrations.
export function AiIntegrationScreen() {
  const navigate = useNavigate();
  const handoff = (useLocation().state ?? {}) as { text?: string; run?: boolean; webhook?: boolean; pickChannel?: string; pickSchema?: string };
  const { channels, isChannelOwner, addMapping, addTemplate, updateTemplate, updateChannel, createChannel, aiSettings, profile } = useStore();
  const owned = channels.filter(isChannelOwner);
  const provider = aiSettings.provider;
  const apiKey = aiSettings.apiKeys[provider] ?? '';
  const { templates: ownedTemplates, loading: templatesLoading } = useOwnedTemplates();

  const [text, setText] = useState(typeof handoff.text === 'string' ? handoff.text : '');
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState<'idle' | 'planning' | 'saving'>('idle');
  const [error, setError] = useState('');
  const [limit, setLimit] = useState('');
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [reuseOff, setReuseOff] = useState<Record<string, boolean>>({}); // similar Signals the user declined to reuse
  const [saveNew, setSaveNew] = useState(true);
  // Per source: which channel it goes to (an owned nickname / NEW_CHANNEL; absent = reuse by name, else create) and the name for a new one.
  const [chanChoice, setChanChoice] = useState<Record<number, string>>({});
  const [chanName, setChanName] = useState<Record<number, string>>({});
  const [metricOff, setMetricOff] = useState<Record<string, boolean>>({}); // metrics the user switched off (by list path)
  const [tplTitle, setTplTitle] = useState('');
  const [notes, setNotes] = useState<string[]>([]);
  const [createdList, setCreatedList] = useState<Mapping[]>([]);
  const [copied, setCopied] = useState('');
  // Destination picked up front: '' = automatic, NEW_CHANNEL = create, else an owned nickname.
  const [pickChannel, setPickChannel] = useState(handoff.pickChannel ?? '');
  const [pickSchema, setPickSchema] = useState(handoff.pickSchema ?? '');

  const readFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_BYTES) return setError('That file is over 2 MB - drop a smaller excerpt.');
    setError('');
    setText(await file.text());
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    void readFile(e.dataTransfer.files[0]);
  };

  const sample = useMemo(() => {
    if (!text.trim()) return null;
    try {
      return parseAutoSample(detectAutoFormat(text), text);
    } catch {
      return null;
    }
  }, [text]);

  // ---- step 1: ask the AI for a proposal (creates nothing) -----------------
  const propose = async () => {
    setError('');
    setBusy('planning');
    const records = !looksLikeDescription(text) && sample && !sample.fieldsOnly && !sample.prom && sample.records.length > 0 ? sample : null;
    const res = await aiSetup(
      provider,
      apiKey,
      records
        ? { fields: [...new Set(records.records.slice(0, 50).flatMap((r) => flattenPaths(r).map((f) => f.path)))], sample: records.records.slice(0, 3) }
        : { text },
    );
    setBusy('idle');
    const out = res.result;
    if (!out) return setError(res.error ?? 'The AI could not set this up.');

    let next: Proposal;
    if (out.mode === 'webhook' && records) {
      const spec = planToSpec(out.plan, '', webhookBodyFormat(records.format), records.recordPath);
      const { produced, tried } = countProducedSignals(spec, records.records);
      if (produced === 0) return setError('The AI mapping did not produce a single Signal from your example, so nothing was proposed.');
      next = { sourceType: 'webhook', title: out.plan.title, description: out.plan.description, spec, records: records.records, targets: [], metrics: [], notes: out.plan.notes ?? [], produced, tried };
    } else if (out.mode === 'scrape') {
      const metrics = (out.metrics ?? []).length >= 3 ? out.metrics! : [];
      const base = { ...planToSpec(out.plan, '', undefined, undefined), intervalSec: out.source.intervalSec };
      const spec = metrics.length > 0 ? metricPreviewSpec(base, metrics[0]) : base;
      const recs = out.records as JsonValue[];
      const { produced, tried } = countProducedSignals(spec, recs);
      if (produced === 0) return setError(`The AI mapped ${out.targets[0]?.url}, but the mapping produced no Signals from the live data, so nothing was proposed.`);
      next = {
        sourceType: `scrape_${out.source.format}`,
        title: out.plan.title,
        description: out.plan.description,
        spec,
        records: recs,
        targets: out.targets,
        metrics,
        intervalSec: out.source.intervalSec,
        notes: [...(out.plan.notes ?? []), ...(metrics.length > 0 && out.metricsSkipped ? [`${out.metricsSkipped} similar list${out.metricsSkipped === 1 ? '' : 's'} did not fit the mapping and ${out.metricsSkipped === 1 ? 'was' : 'were'} left out.`] : []), ...(out.skipped ?? []).map((k) => `Skipped ${k.label || k.url}: ${k.reason}`)],
        produced,
        tried,
      };
    } else {
      return setError('The AI answer did not match what was pasted - try again.');
    }
    setReuseOff({});
    setMetricOff({});
    setChanChoice(pickChannel ? Object.fromEntries((next.sourceType === 'webhook' ? [''] : next.targets).map((_, i) => [i, pickChannel])) : {});
    setChanName({});
    setSaveNew(next.metrics.length <= 30); // hundreds of presets would crowd the channel - opt in
    setTplTitle(next.title);
    setProposal(next);
  };

  // Arrived from the Sources list's "Go": start mapping right away.
  const autoRan = useRef(false);
  useEffect(() => {
    if (autoRan.current || !(handoff.run || handoff.webhook)) return;
    if (handoff.run && !text.trim()) return;
    autoRan.current = true;
    if (handoff.webhook) void quickWebhook();
    else void propose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- the review: what the mapping is, what it makes, what it reuses ------
  const chosenMetrics = useMemo(() => (proposal ? proposal.metrics.filter((m) => !metricOff[m.path]) : []), [proposal, metricOff]);
  const metricNames = useMemo(() => (proposal && proposal.metrics.length > 0 ? chosenMetrics.map((m) => m.name) : undefined), [proposal, chosenMetrics]);
  const baseReview = useMemo(() => (proposal ? buildReview(proposal.spec, proposal.records, ownedTemplates, metricNames) : null), [proposal, ownedTemplates, metricNames]);
  // A fixed Signal name that is merely similar to an existing one is swapped for it unless declined.
  const similar = baseReview?.signals.find((s) => s.status === 'similar' && s.renamable);
  const useSimilar = !!similar && !reuseOff[similar.name];
  const finalSpec = useMemo(() => (proposal ? (useSimilar && similar?.similarTo ? renameConstName(proposal.spec, similar.similarTo) : proposal.spec) : null), [proposal, useSimilar, similar]);
  const review = useMemo(() => (proposal && finalSpec ? buildReview(finalSpec, proposal.records, ownedTemplates, metricNames) : null), [proposal, finalSpec, ownedTemplates, metricNames]);

  const existingTemplates = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of review?.signals ?? []) if (s.status === 'existing' && s.template) m.set(s.template, s.templateTitle ?? s.template);
    return m;
  }, [review]);
  const newNames = (review?.signals ?? []).filter((s) => s.status !== 'existing').map((s) => s.name);
  const sourceLabels = proposal ? (proposal.sourceType === 'webhook' ? [''] : proposal.targets.map((t) => t.label || new URL(t.url).hostname)) : [];
  // One source -> one channel: reuse the owned channel that carries its name, create it otherwise.
  const assignments = useMemo(
    () => assignChannels(proposal?.title ?? '', sourceLabels, owned.map((c) => ({ nickname: c.nickname, title: c.title })), chanChoice, chanName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [proposal, chanChoice, chanName, channels],
  );
  // An all-metrics source needs ceil(metrics / per-mapping cap) mappings.
  const specsPerSource = proposal && chosenMetrics.length > 0 ? metricsSpecs(proposal.spec, chosenMetrics).length : 1;
  const total = proposal && proposal.metrics.length > 0 && chosenMetrics.length === 0 ? 0 : assignments.length * specsPerSource;

  // ---- step 2: confirm - templates first, then the integrations ------------
  const confirm = async () => {
    if (!proposal || !finalSpec || !review) return;
    setError('');
    setBusy('saving');
    const made: Mapping[] = [];
    const extra: string[] = [];
    try {
      const attach = new Set(existingTemplates.keys());
      const chosen = pickSchema && pickSchema !== NEW_CHANNEL ? ownedTemplates.find((t) => t.nickname === pickSchema) : undefined;
      if (chosen) {
        // The user's own schema: new Signals join it instead of a new schema being made.
        attach.add(chosen.nickname);
        if (newNames.length > 0) {
          const next = buildSignalTemplate(chosen.title, chosen.nickname, newNames, uid, profile?.nickname ?? '');
          await updateTemplate({ ...chosen, signals: [...chosen.signals, ...next.signals] });
          extra.push(`Added ${newNames.length} Signal${newNames.length === 1 ? '' : 's'} to schema "${chosen.title}".`);
        }
      }
      const createNames = pickSchema === NEW_CHANNEL ? (review?.signals ?? []).map((x) => x.name) : newNames;
      if (!chosen && (saveNew || pickSchema === NEW_CHANNEL) && createNames.length > 0) {
        const parts = Math.ceil(createNames.length / TEMPLATE_CHUNK);
        for (let i = 0; i < parts; i++) {
          const names = createNames.slice(i * TEMPLATE_CHUNK, (i + 1) * TEMPLATE_CHUNK);
          const title = tplTitle.trim() || proposal.title;
          const tpl = buildSignalTemplate(parts > 1 ? `${title} (${i + 1}/${parts})` : title, resolveNickname('', '@signals', uid().slice(0, 8)), names, uid, profile?.nickname ?? '');
          await addTemplate(tpl);
          attach.add(tpl.nickname);
          extra.push(`Created schema "${tpl.title}" with ${names.length} Signal${names.length === 1 ? '' : 's'}.`);
        }
      }
      // Existing channels that will carry Signals get the templates attached; new ones are created with them.
      const created = new Set<string>();
      for (const a of assignments) {
        if (created.has(a.nickname)) continue;
        created.add(a.nickname);
        if (a.isNew) {
          await createChannel({ ...blankDraft(), title: a.title, nickname: a.nickname, description: proposal.description || `Signals from ${proposal.title}`, templateIds: [...attach] });
          extra.push(`Created channel ${a.nickname}.`);
        } else {
          const ch = channels.find((c) => c.nickname === a.nickname);
          const missing = [...attach].filter((t) => !(ch?.templateIds ?? []).includes(t));
          if (ch && missing.length > 0) await updateChannel({ ...ch, templateIds: [...(ch.templateIds ?? []), ...missing] });
        }
      }

      const sources = proposal.sourceType === 'webhook' ? [{ url: '', label: '' }] : proposal.targets;
      const many = sources.length > 1;
      for (const [i, t] of sources.entries()) {
        const ch = assignments[i].nickname;
        const parts = proposal.metrics.length > 0 ? metricsSpecs(finalSpec, chosenMetrics) : [finalSpec];
        for (const [pi, part] of parts.entries()) {
          const base = { ...part, channel: ch, ...(t.url ? { target: t.url } : {}) };
          const spec = many ? withEntityLabel(base, t.label || new URL(t.url).hostname) : base;
          const partTitle = parts.length > 1 ? ` (${pi + 1}/${parts.length})` : '';
          made.push(
            await addMapping({
              id: uid(),
              nickname: `@ai-${uid().slice(0, 8)}`,
              isPublic: false,
              title: `${many && t.label ? `${proposal.title} · ${t.label}` : proposal.title}${partTitle}`,
              description: proposal.description || `AI-mapped ${t.url || 'webhook'} -> #${ch}`,
              sourceType: proposal.sourceType,
              spec: spec as Record<string, unknown>,
            }),
          );
        }
      }
      setNotes([...extra, ...(proposal.sourceType === 'webhook' ? [] : [`Polling ${sources.length} source${many ? 's' : ''} every ${proposal.intervalSec}s${proposal.metrics.length > 0 ? `, ${chosenMetrics.length} metrics each` : ''}.`])]);
    } catch (err) {
      if (err instanceof LimitError) {
        setLimit(err.message);
        extra.push(`Stopped after ${made.length} integration${made.length === 1 ? '' : 's'}: connector limit reached.`);
        setNotes(extra);
      } else {
        setError(err instanceof Error ? err.message : 'Unable to create this integration.');
      }
    } finally {
      if (made.length > 0) setCreatedList(made);
      setBusy('idle');
    }
  };

  // No AI: an immediate webhook (name/value rules) with a Bearer token, named from the box.
  const quickWebhook = async () => {
    setError('');
    setBusy('saving');
    const made: Mapping[] = [];
    const extra: string[] = [];
    try {
      const name = webhookName(text);
      let channel = pickChannel && pickChannel !== NEW_CHANNEL ? pickChannel : '';
      if (!channel) {
        const a = assignChannels(name, [''], owned.map((c) => ({ nickname: c.nickname, title: c.title })), pickChannel ? { 0: NEW_CHANNEL } : {}, {})[0];
        channel = a.nickname;
        if (a.isNew) {
          await createChannel({ ...blankDraft(), title: a.title, nickname: a.nickname, description: `Signals from ${name}`, templateIds: pickSchema && pickSchema !== NEW_CHANNEL ? [pickSchema] : [] });
          extra.push(`Created channel ${a.nickname}.`);
        }
      }
      if (pickSchema && pickSchema !== NEW_CHANNEL) {
        const ch = channels.find((c) => c.nickname === channel);
        if (ch && !(ch.templateIds ?? []).includes(pickSchema)) await updateChannel({ ...ch, templateIds: [...(ch.templateIds ?? []), pickSchema] });
      }
      made.push(
        await addMapping({
          id: uid(),
          nickname: `@ai-${uid().slice(0, 8)}`,
          isPublic: false,
          title: name,
          description: `Webhook -> #${channel}`,
          sourceType: 'webhook',
          spec: { channel, provider: 'generic', rules: [{ to: 'name', from: 'name' }, { to: 'value', from: 'value', cast: 'number' }] } as Record<string, unknown>,
        }),
      );
      setNotes([...extra, `Webhook "${name}" sends to ${channel}. Fields: name and value (adjust the mapping in the integration).`]);
    } catch (err) {
      if (err instanceof LimitError) setLimit(err.message);
      else setError(err instanceof Error ? err.message : 'Unable to create the webhook.');
    } finally {
      if (made.length > 0) setCreatedList(made);
      setBusy('idle');
    }
  };

  // ---- done ------------------------------------------------------------------
  if (createdList.length > 0) {
    const first = createdList[0];
    const webhook = first.sourceType === 'webhook';
    return (
      <div className="flex flex-col gap-4">
        <ScreenHeader title="AI integration" backTo="/integrations" />
        <UpgradePrompt open={!!limit} onClose={() => setLimit('')} message={limit} title="Connector limit reached" />
        <FormSection title={webhook ? 'Mapped and listening' : `Mapped and polling (${createdList.length})`}>
          <ul className="text-sm text-black/60 dark:text-white/60 m-0 pl-4">
            {notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
          {webhook ? (
            <>
              <p className="text-xs text-black/45 dark:text-white/45 m-0">
                Copy each token now - it won't be shown again. Send it as <code className="font-mono">Authorization: Bearer &lt;token&gt;</code> with a body shaped like your example.
              </p>
              {createdList.map((m) => (
                <div key={m.id} className="flex flex-col gap-1.5">
                  {createdList.length > 1 && <span className="text-xs font-medium text-black/60 dark:text-white/60">#{String(m.spec.channel ?? '')}</span>}
                  <code className="rounded-lg bg-black/[0.06] dark:bg-white/10 px-3 py-2 text-[13px] font-mono select-all break-all">{`POST /ingest/webhook/${m.nickname}`}</code>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 min-w-0 truncate rounded-lg bg-black/[0.06] dark:bg-white/10 px-3 py-2 text-[13px] font-mono select-all">{m.ingestToken}</code>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        void navigator.clipboard?.writeText(m.ingestToken ?? '').then(() => setCopied(m.id), () => undefined);
                      }}
                    >
                      {copied === m.id ? <Check size={16} /> : <Copy size={16} />}
                      {copied === m.id ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <p className="text-sm text-black/60 dark:text-white/60 m-0">Zmist fetches the source(s) on its own schedule - nothing to run. If one needs a token or header, add it in that integration's credentials.</p>
              <ul className="m-0 pl-4 text-sm">
                {createdList.map((m) => (
                  <li key={m.id}>
                    <Link to={`/integrations/${m.id}`} className="underline">{m.title}</Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </FormSection>
        <Badge>Configured</Badge>
        <Button onClick={() => navigate(createdList.length === 1 ? `/integrations/${first.id}` : '/integrations')} full>
          {createdList.length === 1 ? 'Open the integration to review the mapping' : 'Go to integrations'}
        </Button>
      </div>
    );
  }

  // ---- review ----------------------------------------------------------------
  if (proposal && review && finalSpec) {
    return (
      <div className="flex flex-col gap-4">
        <ScreenHeader title="Review the mapping" backTo="/integrations/catalog" />
        <UpgradePrompt open={!!limit} onClose={() => setLimit('')} message={limit} title="Connector limit reached" />
        <p className="text-sm text-black/60 dark:text-white/60 m-0">
          <span className="font-medium text-black/80 dark:text-white/80">{proposal.title}</span> - {proposal.description || 'AI-proposed mapping'}. Nothing is created until you confirm.
        </p>
        {proposal.notes.length > 0 && (
          <ul className="m-0 pl-4 text-xs text-amber-700 dark:text-amber-400 flex flex-col gap-0.5">
            {proposal.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        )}

        <div className={cardCls}>
          <h3 className={h3Cls}>Where each field goes</h3>
          {review.rows.filter((r) => proposal.metrics.length === 0 || r.to !== 'name').map((r) => (
            <div key={r.to} className="grid gap-0.5 sm:grid-cols-[9rem_minmax(0,1fr)] text-[13px]">
              <span className="font-medium">{r.title}</span>
              <span className="min-w-0">
                <code className="font-mono text-[12px]">{r.source}</code>
                {r.example && <span className="text-black/50 dark:text-white/50"> · e.g. {r.example}</span>}
                {r.how && <span className="text-black/40 dark:text-white/40"> · {r.how}</span>}
              </span>
            </div>
          ))}
          {review.presentation.map((line) => (
            <p key={line} className="text-xs text-black/55 dark:text-white/55 m-0">
              {line}
            </p>
          ))}
        </div>

        {proposal.metrics.length > 0 && (
          <div className={cardCls}>
            <h3 className={h3Cls}>
              {chosenMetrics.length} of {proposal.metrics.length} metrics selected
            </h3>
            <p className="text-xs text-black/50 dark:text-white/50 m-0">
              Every list in the document shaped like the one mapped above is a metric of its own, named after it. Untick the ones you don't need. Each poll takes the newest values of each.
            </p>
            <MetricChecklist metrics={proposal.metrics} off={metricOff} onChange={setMetricOff} />
            {chosenMetrics.length === 0 && <p role="alert" className="text-xs text-red-600 dark:text-red-400 m-0">Select at least one metric.</p>}
          </div>
        )}

        <div className={cardCls}>
          <h3 className={h3Cls}>What Zmist will create from your data</h3>
          <p className="text-xs text-black/50 dark:text-white/50 m-0">
            {proposal.produced} of {proposal.tried} sampled records make a Signal. The first few:
          </p>
          {review.preview.map((s, i) => (
            <div key={i} className="text-[13px] flex flex-wrap gap-x-2">
              <span className="font-medium">{s.name}</span>
              <span>= {s.value}</span>
              <span className="text-black/45 dark:text-white/45">{s.atSynthesized ? 'at arrival time' : s.at}</span>
              {s.labels.length > 0 && <span className="text-black/45 dark:text-white/45">{s.labels.join(', ')}</span>}
            </div>
          ))}
        </div>

        <div className={cardCls}>
          <h3 className={h3Cls}>Signals and schemas</h3>
          {templatesLoading && <p className="text-xs text-black/50 dark:text-white/50 m-0">Looking through your schemas…</p>}
          {(baseReview?.signals ?? []).slice(0, proposal.metrics.length > 0 ? 12 : undefined).map((s) => {
            const swapped = s.status === 'similar' && s.renamable && !reuseOff[s.name];
            return (
              <div key={s.name} className="flex flex-col gap-0.5 text-[13px]">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{swapped ? s.similarTo : s.name}</span>
                  {s.status === 'existing' && <Badge>Already in “{s.templateTitle}”</Badge>}
                  {swapped && <Badge>Already in “{s.similarTemplateTitle}”</Badge>}
                  {s.status !== 'existing' && !swapped && <Badge>New Signal</Badge>}
                </span>
                {s.status === 'similar' && (
                  <span className="text-xs text-black/55 dark:text-white/55">
                    {swapped ? `You wrote “${s.name}”; ` : ''}
                    Looks like “{s.similarTo}” in “{s.similarTemplateTitle}”.
                    {s.renamable && (
                      <label className="ml-1 inline-flex items-center gap-1 cursor-pointer text-blue-600 dark:text-blue-400">
                        <input type="checkbox" checked={swapped} onChange={(e) => setReuseOff((p) => ({ ...p, [s.name]: !e.target.checked }))} />
                        Use “{s.similarTo}” instead
                      </label>
                    )}
                  </span>
                )}
              </div>
            );
          })}
          {existingTemplates.size > 0 && (
            <p className="text-xs text-black/50 dark:text-white/50 m-0">
              Existing: {[...existingTemplates.values()].map((t) => `“${t}”`).join(', ')} will be attached to the destination channel(s) if they aren't already.
            </p>
          )}
          <SchemaSelect templates={ownedTemplates} value={pickSchema} onChange={setPickSchema} />
          {newNames.length > 0 && !pickSchema && (
            <div className="flex flex-col gap-1.5 pt-1">
              <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer">
                <input type="checkbox" checked={saveNew} onChange={(e) => setSaveNew(e.target.checked)} />
                Save the {newNames.length} new Signal{newNames.length === 1 ? '' : 's'} in a new template
              </label>
              {saveNew && <TextField aria-label="New schema name" value={tplTitle} onChange={(e) => setTplTitle(e.target.value)} placeholder="Schema name" />}
            </div>
          )}
        </div>

        <div className={cardCls}>
          <h3 className={h3Cls}>Where it goes</h3>
          <p className="text-xs text-black/50 dark:text-white/50 m-0">
            Each source gets its own channel - one you already have with that name, or a new one. {proposal.sourceType === 'webhook' ? 'You will get a webhook URL and token for it.' : `Zmist polls every ${proposal.intervalSec}s.`}
          </p>
          {assignments.map((a, i) => (
            <div key={i} className="flex flex-col gap-1.5 text-[13px]">
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{sourceLabels[i] || 'Your data'}</span>
                <ArrowRight size={14} aria-hidden className="text-black/40 dark:text-white/40" />
                <span>{a.nickname}</span>
                <Badge>{a.isNew ? 'New channel' : a.matched ? 'Existing channel' : 'Existing channel (your pick)'}</Badge>
              </span>
              <Select
                aria-label={`Channel for ${sourceLabels[i] || 'your data'}`}
                value={a.isNew ? NEW_CHANNEL : a.nickname}
                onChange={(e) => setChanChoice((p) => ({ ...p, [i]: e.target.value }))}
              >
                <option value={NEW_CHANNEL}>Create a new channel</option>
                {owned.map((c) => (
                  <option key={c.nickname} value={c.nickname}>
                    Use #{c.nickname.replace(/^@/, '')}
                  </option>
                ))}
              </Select>
              {a.isNew && (
                <TextField
                  aria-label={`New channel name for ${sourceLabels[i] || 'your data'}`}
                  value={chanName[i] ?? ''}
                  onChange={(e) => setChanName((p) => ({ ...p, [i]: e.target.value }))}
                  placeholder={a.title}
                />
              )}
            </div>
          ))}
          <p className="text-[13px] m-0">
            {total} integration{total === 1 ? '' : 's'} will be created{assignments.some((a) => a.isNew) ? `, ${new Set(assignments.filter((a) => a.isNew).map((a) => a.nickname)).size} new channel(s)` : ''}.
          </p>
        </div>

        {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400 m-0">{error}</p>}
        <Button onClick={() => void confirm()} disabled={busy !== 'idle' || templatesLoading || total === 0} full>
          {busy === 'saving' ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
          {busy === 'saving' ? 'Creating…' : `Create ${total} integration${total === 1 ? '' : 's'}`}
        </Button>
        <Button variant="secondary" onClick={() => setProposal(null)} disabled={busy !== 'idle'} full>
          Back
        </Button>
      </div>
    );
  }

  // ---- input -----------------------------------------------------------------
  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader title="AI integration" backTo="/integrations/catalog" />
      <UpgradePrompt open={!!limit} onClose={() => setLimit('')} message={limit} title="Connector limit reached" />
      <FormSection title="Your data">
        <div onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop} className={`rounded-xl ${dragging ? 'ring-2 ring-blue-500' : ''}`}>
          <TextArea aria-label="Data to integrate" value={text} onChange={(e) => setText(e.target.value)} rows={9} placeholder={'Paste records, or describe a data source (with its URL), or drop a file - then press the button.\n\nTrack revenue at https://… for Apple\nTrack all metrics from https://… for Apple\n\nOr records, e.g.\n{"host":"web-1","cpu_pct":42.5,"ts":"2026-10-11T10:00:00Z"}'} />
        </div>
      </FormSection>
      <FormSection title="Where it goes (optional)">
        <ChannelSelect channels={owned} value={pickChannel} onChange={setPickChannel} />
        <SchemaSelect templates={ownedTemplates} value={pickSchema} onChange={setPickSchema} />
      </FormSection>
      <AiProviderPicker />
      <p className="text-xs text-black/45 dark:text-white/45 m-0">
        The AI ({AI_PROVIDER_LABEL[provider]}) sees only the first three records and the field names. It may only use a fixed rule vocabulary and real field paths. You'll review
        the mapping, its Signals, schemas and channels before anything is created.
      </p>
      {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400 m-0">{error}</p>}
      <Button onClick={() => void propose()} disabled={busy !== 'idle' || !text.trim()} full>
        {busy === 'idle' ? <Sparkles size={16} /> : <Loader2 size={16} className="animate-spin" />}
        {busy === 'planning' ? 'Mapping…' : 'Integration'}
        {busy === 'idle' && <ArrowRight size={16} aria-hidden />}
      </Button>
      <Button variant="secondary" onClick={() => void quickWebhook()} disabled={busy !== 'idle'} full>
        <Webhook size={16} /> Get a webhook + Bearer token now
      </Button>
      <p className="text-xs text-black/45 dark:text-white/45 m-0 -mt-2">No AI: creates the webhook right away, named after the first line of the box (or a random name), with the channel and schema above.</p>
    </div>
  );
}
